import { debounce } from "lodash";
import React from "react";
import * as XLSX from "xlsx";

import { addCommas, convertToPercent } from "../../helper/number.helpers";
import { normalizeZipCode } from "../../pages/townson-dashboard-list/components/client-data-upload-component/helpers.add-eddit-location";
import { ToastShow } from "../../redux/toast.duck";
import { getBackendGeocode } from "../../services/dashboards/townson-dashboard.services/geocode.service";
import {
	createDashboard,
	editDashboardDetails,
	getSOCCode,
	validateCSVFile,
} from "../../services/dashboards/townson-dashboard.services/townson-dashboard-data";

export const handleSocCodeUpdate = (newSelectedSocCode, setSelectedSocCode) => {
	setSelectedSocCode(newSelectedSocCode);
};

export const handleOnCurrentLocationDescriptionChange = (
	name,
	value,
	currentLocationDescription,
	setCurrentLocationDescription,
	checkUrlsForMaliciousContent,
) => {
	if (name === "notes") {
		const regex = new RegExp(
			"https?:\\/\\/[^\\s/$.?#].[^\\s]*|ftps?:\\/\\/[^\\s/$.?#].[^\\s]*|(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{2,256}\\.[a-z]{2,6}\\b([-a-zA-Z0-9@:%_\\+.~#?&//=]*)",
			"gi",
		);

		const urls = value.match(regex) || [];
		if (urls.length > 0) {
			const debouncedCheckUrlsForMaliciousContent = debounce(checkUrlsForMaliciousContent, 2000);
			debouncedCheckUrlsForMaliciousContent(urls);
		}
	}

	setCurrentLocationDescription({
		...currentLocationDescription,
		[name]: value,
	});
};

export const uploadCSV = async (file, setProgressFileUpload, setCsvFile, setCsvFileName, setErrorFileUpload) => {
	if (!file) return;
	const formData = new FormData();
	formData.append("employee_csv", file);
	const res = await validateCSVFile(formData, setProgressFileUpload);
	if (res?.status === "success") {
		setCsvFile(res?.data?.employee_csv_path);
	} else {
		setCsvFileName();
		setCsvFile();
		setErrorFileUpload(res.message);
	}
};

export const handleOnChangeFile = (event, setCsvFile, setCsvFileName, setErrorFileUpload, uploadCSV, removeCSV) => {
	if (event && event.target && event.target.files[0]) {
		setErrorFileUpload();
		setCsvFileName(event.target.files[0]?.name);
		uploadCSV(event.target.files[0]);
	} else if (event && event.name) {
		setErrorFileUpload();
		setCsvFileName(event.name);
		uploadCSV(event);
	} else {
		removeCSV();
		setCsvFile();
		setCsvFileName();
	}
};

const allowedExtensions = ["csv", "xlsx", "xls", "xlsm", "xlsb"];

export const geocodeReviewRows = async (rows, maxAllowed) => {
	const locations = rows.slice(0, maxAllowed).map((row) => ({
		address: `${row.Location.replace("#", "")}.json?limit=${maxAllowed}&countrySet=US`,
	}));

	if (!locations.length) return [];

	const response = await getBackendGeocode({ locations });
	return rows.slice(0, maxAllowed).map((row, index) => {
		const results = response?.batchItems?.[index]?.response?.results || [];
		const normalizedZip = normalizeZipCode(row.sourceZip);
		const normalizeStreetForComparison = (street) =>
			normalizeStreetType(street || "")
				.replace(/[-\s]/g, "")
				.toUpperCase();
		const addressMatch = results.map((result) => {
			const freeformAddress = result?.address?.freeformAddress || "";
			const responseAddressParts = freeformAddress
				.split(",")
				.map((part) => part.trim())
				.filter((part) => part !== "United States");
			const addressParts = freeformAddress.split(" ");
			const responseZip = normalizeZipCode(addressParts[addressParts.length - 1]?.trim());
			const streetFromResponse = responseAddressParts[0] || "";
			const lastPart = responseAddressParts[responseAddressParts.length - 1] || "";
			const responseState = /^\d{5}(-\d{4})?$/.test(lastPart)
				? (responseAddressParts[responseAddressParts.length - 2] || "").split(" ")[0]
				: lastPart.split(" ")[0] || "";
			const responseCity = responseAddressParts[1] || "";
			const sameCityStateZip =
				responseCity.toLowerCase() === row.sourceCity.toLowerCase() &&
				normalizeState(responseState) === normalizeState(row.sourceState) &&
				responseZip === normalizedZip;
			const sameStreetAndZip =
				normalizeStreetForComparison(streetFromResponse) ===
					normalizeStreetForComparison(row.sourcePropertyAddress) && responseZip === normalizedZip;
			return { result, sameCityStateZip, sameStreetAndZip };
		});
		const exactMatch = addressMatch.find((match) => match.sameStreetAndZip)?.result;
		const partialMatch = addressMatch.find((match) => match.sameCityStateZip)?.result;
		const suggestedResult = exactMatch || partialMatch || results[0];
		const position = exactMatch?.position;
		const geocodeStatus = exactMatch ? "Geocoded" : partialMatch ? "Partial match" : "No match";
		console.log("[CSV geocode debug] classification", {
			rowIndex: index,
			importedLocation: row.Location,
			sourcePropertyAddress: row.sourcePropertyAddress,
			sourceCity: row.sourceCity,
			sourceState: row.sourceState,
			sourceZip: row.sourceZip,
			suggestedAddress: suggestedResult?.address?.freeformAddress ?? "",
			geocodeStatus,
			hasExactMatch: Boolean(exactMatch),
			hasPartialMatch: Boolean(partialMatch),
			coordinates: position ?? null,
		});
		return {
			...row,
			rowIndex: index,
			geocodedAddress: suggestedResult?.address?.freeformAddress ?? "",
			latitude: position?.lat ?? null,
			longitude: position?.lon ?? null,
			geocodeStatus,
			geocodeError: exactMatch
				? ""
				: partialMatch
					? "The city, state, and ZIP matched, but the street address requires review."
					: "The geocoded result did not match the uploaded address.",
			approved: false,
		};
	});
};

export const handleOnChangeLocationFile = (
	event,
	setApprovingCsvUpload,
	setProceedWithCsvUpload,
	setCsvError,
	setTempData,
	setCSVProcessingLoading,
	setCsvApproval,
	setTempResults,
	setNumberOfRows,
	maxAllowed,
	officeLocationType = false,
) => {
	const validateFileExtension = (fileName) => {
		const extension = fileName.split(".").pop().toLowerCase();
		return allowedExtensions.includes(extension);
	};

	const validateAddress = (address, city, state, zip, rowIndex) => {
		const errors = [];

		if (!address?.trim()) errors.push("Property Address is required");
		if (!city?.trim()) errors.push("City is required");
		if (!state?.trim()) errors.push("State is required");
		if (!zip?.trim()) errors.push("ZIP code is required");

		if (address?.trim() === "123 StreetName") errors.push("Please replace default Property Address");
		if (city?.trim() === "City") errors.push("Please replace default City");
		if (state?.trim() === "State") errors.push("Please replace default State");
		if (zip?.trim() === "12345") errors.push("Please replace default ZIP code");

		if (errors.length > 0) {
			throw new Error(`Row ${rowIndex}: ${errors.join(". ")}`);
		}
	};

	const processRow = (row, headers, locationType, rowIndex) => {
		const getValue = (fieldName) => {
			const index = headers.findIndex((header) => header.trim() === fieldName.trim());
			const value = index !== -1 ? row[index] : "";
			return (
				value
					?.toString()
					.replace(/[\r\n]+/g, " ")
					.replace(/\s+/g, " ")
					.trim() || ""
			);
		};

		const propertyAddress = getValue("Property Address");
		const city = getValue("City");
		const state = getValue("State");
		const zip = getValue("Zip");

		validateAddress(propertyAddress, city, state, zip, rowIndex);

		const baseObj = {
			Location: `${propertyAddress}${propertyAddress && ", "}${city}${city && ", "}${state} ${zip}`,
			sourcePropertyAddress: propertyAddress,
			sourceCity: city,
			sourceState: state,
			sourceZip: zip,
			status: getValue("Building Status"),
			opex_value: getValue("OPEX"),
			leasing_company: getValue("Leasing Company"),
			notes: getValue("Notes"),
			is_current: getValue("Current Location (Yes/No)").toLowerCase() === "yes",
			building_sf: getValue("Building SF"),
			available_sf: getValue("Available SF"),
			base_rent: getValue("Base Rent"),
			gross_price_sf: getValue("Gross PSF"),
		};

		const typeSpecificObj = locationType
			? {
					// Industrial fields
					office_sf: getValue("Office SF"),
					clear_height: getValue("Clear Height"),
					car_trailer_parking: getValue("Car/Trailer Parking"),
					docs_grade_level_doors: getValue("Docks/Grade Level Doors"),
			  }
			: {
					// Office fields
					year_built: getValue("Year Built"),
					parking_ratio: getValue("Parking Ratio"),
					location_suite: getValue("Suite"),
					percent_leased: convertToPercent(getValue("Percent Leased")),
					electric_price_sf: getValue("Electric Price SF"),
			  };

		return {
			...baseObj,
			...typeSpecificObj,
		};
	};
	const handleFile = async (file) => {
		if (!validateFileExtension(file.name)) {
			throw new Error(`Invalid file type. Allowed extensions are: ${allowedExtensions.join(", ")}`);
		}

		const reader = new FileReader();

		reader.onload = async (evt) => {
			try {
				const wb = XLSX.read(evt.target.result, { type: "binary" });

				if (!wb?.SheetNames?.length) {
					throw new Error("Please select valid proposed location file");
				}

				const ws = wb.Sheets[wb.SheetNames[0]];
				const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", blankrows: false });

				if (!data?.length) {
					throw new Error("Please select valid proposed location file");
				}

				const headers = data[0];

				const headerSet = new Set(headers.map((h) => h.trim()));

				const industrialOnlyFields = new Set([
					"Office SF",
					"Clear Height",
					"Car/Trailer Parking",
					"Docks/Grade Level Doors",
				]);

				const officeOnlyFields = new Set([
					"Year Built",
					"Parking Ratio",
					"Suite",
					"Percent Leased",
					"Electric Price SF",
				]);

				const hasIndustrialFields = Array.from(industrialOnlyFields).some((field) => headerSet.has(field));
				const hasOfficeFields = Array.from(officeOnlyFields).some((field) => headerSet.has(field));

				let detectedType;
				if (hasIndustrialFields && !hasOfficeFields) {
					detectedType = "industrial";
				} else if (hasOfficeFields && !hasIndustrialFields) {
					detectedType = "office";
				} else if (!hasIndustrialFields && !hasOfficeFields) {
					throw new Error("Invalid template: Missing required fields for both office and industrial types");
				} else {
					throw new Error(
						"Invalid template: Contains mixed fields from both office and industrial templates",
					);
				}

				if (
					(detectedType === "industrial" && !officeLocationType) ||
					(detectedType === "office" && officeLocationType)
				) {
					throw new Error(
						`Template mismatch: ${
							officeLocationType ? "industrial" : "office"
						} location type selected but uploaded ${detectedType} template. Please use the correct template for the selected location type.`,
					);
				}

				setTempData(data);

				const totalRows = data.length - 1;
				const maxRows = Math.min(totalRows + 1, maxAllowed + 1);
				setNumberOfRows(totalRows);

				const results = [];
				for (let i = 1; i < maxRows; i++) {
					const row = data[i];

					try {
						const processedRow = processRow(row, headers, officeLocationType, i);
						results.push(processedRow);
					} catch (error) {
						throw new Error(`Error processing row ${i}: ${error.message}`);
					}
				}

				if (results.length > 0) {
					setCSVProcessingLoading(true);
					const reviewRows = await geocodeReviewRows(results, maxAllowed);
					setCsvApproval(reviewRows);
					setApprovingCsvUpload(true);
					setTempResults(reviewRows);
					setCSVProcessingLoading(false);
				}
			} catch (error) {
				setCsvError([error.message]);
				setApprovingCsvUpload(false);
				setProceedWithCsvUpload(false);
			}
		};

		reader.readAsBinaryString(file);
	};

	if (event?.target?.files?.[0]) {
		setApprovingCsvUpload(false);
		setProceedWithCsvUpload(false);
		setCsvError([]);

		handleFile(event.target.files[0]).catch((error) => {
			setCsvError([error.message]);
		});
	}
};

export const uploadCustomers = async (
	file,
	setCustomersProgressFileUpload,
	setCustomersFile,
	setCustomersFileName,
	setCustomersErrorFileUpload,
) => {
	if (!file) return;
	const formData = new FormData();
	formData.append("customer_csv", file);
	const res = await validateCSVFile(formData, setCustomersProgressFileUpload);
	if (res?.status === "success") {
		setCustomersFile(res?.data?.customer_csv_path);
	} else {
		setCustomersFileName();
		setCustomersFile();
		setCustomersErrorFileUpload(res.message);
	}
};

export const handleCustomersOnChangeFile = (
	event,
	setCustomersErrorFileUpload,
	setCustomersFileName,
	setCustomersFile,
	uploadCustomers,
	removeCustomers,
) => {
	if (event && event.target && event.target.files[0]) {
		setCustomersErrorFileUpload();
		setCustomersFileName(event.target.files[0]?.name);
		uploadCustomers(event.target.files[0]);
	} else if (event && event.name) {
		setCustomersErrorFileUpload();
		setCustomersFileName(event.name);
		uploadCustomers(event);
	} else {
		removeCustomers();
		setCustomersFile();
		setCustomersFileName();
	}
};

const normalizeStreetType = (address) => {
	return address
		.replace(/\bLane\b/gi, "Ln")
		.replace(/\bStreet\b/gi, "St")
		.replace(/\bAvenue\b/gi, "Ave")
		.replace(/\bDrive\b/gi, "Dr")
		.replace(/\bRoad\b/gi, "Rd")
		.replace(/\bWest\b/gi, "W")
		.replace(/\bEast\b/gi, "E")
		.replace(/\bNorth\b/gi, "N")
		.replace(/\bSouth\b/gi, "S");
};

const normalizeState = (state) => {
	if (!state) return "";

	const stateUpper = state.toString().trim().toUpperCase();

	const stateMapping = {
		"ALABAMA": "AL",
		"ALASKA": "AK",
		"ARIZONA": "AZ",
		"ARKANSAS": "AR",
		"CALIFORNIA": "CA",
		"COLORADO": "CO",
		"CONNECTICUT": "CT",
		"DELAWARE": "DE",
		"FLORIDA": "FL",
		"GEORGIA": "GA",
		"HAWAII": "HI",
		"IDAHO": "ID",
		"ILLINOIS": "IL",
		"INDIANA": "IN",
		"IOWA": "IA",
		"KANSAS": "KS",
		"KENTUCKY": "KY",
		"LOUISIANA": "LA",
		"MAINE": "ME",
		"MARYLAND": "MD",
		"MASSACHUSETTS": "MA",
		"MICHIGAN": "MI",
		"MINNESOTA": "MN",
		"MISSISSIPPI": "MS",
		"MISSOURI": "MO",
		"MONTANA": "MT",
		"NEBRASKA": "NE",
		"NEVADA": "NV",
		"NEW HAMPSHIRE": "NH",
		"NEW JERSEY": "NJ",
		"NEW MEXICO": "NM",
		"NEW YORK": "NY",
		"NORTH CAROLINA": "NC",
		"NORTH DAKOTA": "ND",
		"OHIO": "OH",
		"OKLAHOMA": "OK",
		"OREGON": "OR",
		"PENNSYLVANIA": "PA",
		"RHODE ISLAND": "RI",
		"SOUTH CAROLINA": "SC",
		"SOUTH DAKOTA": "SD",
		"TENNESSEE": "TN",
		"TEXAS": "TX",
		"UTAH": "UT",
		"VERMONT": "VT",
		"VIRGINIA": "VA",
		"WASHINGTON": "WA",
		"WEST VIRGINIA": "WV",
		"WISCONSIN": "WI",
		"WYOMING": "WY",
		"DISTRICT OF COLUMBIA": "DC",
		"WASHINGTON DC": "DC",
		"WASHINGTON D.C.": "DC",
	};

	const validAbbreviations = Object.values(stateMapping);

	if (validAbbreviations.includes(stateUpper)) {
		return stateUpper;
	}

	return stateMapping[stateUpper] || stateUpper;
};

export const resumeProcessing = async (
	setCSVProcessingLoading,
	setCsvError,
	setLocation,
	setProposedLocationIndex,
	setLocationDescription,
	tempResults,
	maxAllowed,
	tempData,
) => {
	const tempLocation = [];
	const tempDescription = [];
	const input = [];
	for (let i = 0; i < tempResults?.length; i++) {
		if (i < maxAllowed && tempResults[i]?.Location && tempResults[i]?.latitude == null) {
			input.push({
				address: `${tempResults[i]?.Location.replace("#", "")}.json?limit=${maxAllowed}&countrySet=US`,
			});
		}
	}
	setCSVProcessingLoading(true);

	const apiData = input.length ? await getBackendGeocode({ locations: input }) : { batchItems: [] };

	const error = ["ERROR:"];
	for (let i = 0; i < tempResults?.length; i++) {
		if (i < maxAllowed && tempData[i + 1][3]) {
			// Trim all tempData values before processing
			const propertyAddress = tempData[i + 1][0]?.toString().trim() || "";
			const city = tempData[i + 1][1]?.toString().trim() || "";
			const state = tempData[i + 1][2]?.toString().trim() || "";
			const zip = tempData[i + 1][3]?.toString().trim() || "";

			const normalizedZip = normalizeZipCode(zip);

			const approvedResult =
				tempResults[i]?.latitude != null
					? {
							address: {
								freeformAddress: tempResults[i].manualAddress || tempResults[i].geocodedAddress,
							},
							position: { lat: tempResults[i].latitude, lon: tempResults[i].longitude },
					  }
					: null;
			const apiResponse =
				approvedResult ||
				apiData?.batchItems[i]?.response?.results.find((arg) => {
					const addressParts = arg?.address?.freeformAddress.split(" ");
					const responseZip = normalizeZipCode(addressParts[addressParts.length - 1]?.trim());

					const streetFromResponse = arg?.address?.freeformAddress.split(",")[0].trim();

					// Remove "United States" from our address for testing
					const responseAddressParts = arg?.address?.freeformAddress
						.split(",")
						.map((part) => part.trim())
						.filter((part) => part !== "United States");

					let responseState = "";
					let responseCity = "";

					if (responseAddressParts.length >= 3) {
						const lastPart = responseAddressParts[responseAddressParts.length - 1];

						// Check if last part is just ZIP (5 or 5+4 digits) or contains state
						if (/^\d{5}(-\d{4})?$/.test(lastPart)) {
							// Last part is ZIP only, state should be in the part before
							const statePart = responseAddressParts[responseAddressParts.length - 2];
							// Get first word (state)
							responseState = statePart.split(" ")[0];
						} else {
							// Last part contains "State ZIP" together
							const parts = lastPart.split(" ");
							responseState = parts[0];
						}

						responseCity = responseAddressParts[1] || "";
					}

					const normalizeStreetForComparison = (street) => {
						return normalizeStreetType(street).replace(/[-\s]/g, "").toUpperCase();
					};

					const condition1 =
						responseCity.toLowerCase() === city.toLowerCase() &&
						normalizeState(responseState) === normalizeState(state) &&
						normalizeZipCode(responseZip) === normalizedZip;

					const condition2 =
						normalizeStreetForComparison(streetFromResponse) ===
							normalizeStreetForComparison(propertyAddress) &&
						normalizeZipCode(responseZip) === normalizedZip;
					return condition1 || condition2;
				});

			if (apiResponse?.position) {
				tempDescription.push({
					notes: tempResults[i]["notes"] ? tempResults[i]["notes"] : "",
					opex_value: parseFloat(tempResults[i]["opex_value"]) > 0.0 ? tempResults[i]["opex_value"] : "",
					year_built: parseFloat(tempResults[i]["year_built"]) > 0.0 ? tempResults[i]["year_built"] : "",
					building_sf: parseFloat(tempResults[i]["building_sf"]) > 0.0 ? tempResults[i]["building_sf"] : "",
					parking_ratio: tempResults[i]["parking_ratio"] ? tempResults[i]["parking_ratio"] : "",
					available_sf:
						parseFloat(tempResults[i]["available_sf"]) > 0.0 ? tempResults[i]["available_sf"] : "",
					office_sf: parseFloat(tempResults[i]["office_sf"]) > 0.0 ? tempResults[i]["office_sf"] : "",
					clear_height:
						parseFloat(tempResults[i]["clear_height"]) > 0.0 ? tempResults[i]["clear_height"] : "",
					car_trailer_parking:
						parseFloat(tempResults[i]["car_trailer_parking"]) > 0.0
							? tempResults[i]["car_trailer_parking"]
							: "",
					docs_grade_level_doors:
						parseFloat(tempResults[i]["docs_grade_level_doors"]) > 0.0
							? tempResults[i]["docs_grade_level_doors"]
							: "",
					base_rent: parseFloat(tempResults[i]["base_rent"]) > 0.0 ? tempResults[i]["base_rent"] : "",
					location_suite: tempResults[i]["location_suite"] ? tempResults[i]["location_suite"] : "",
					percent_leased: tempResults[i]["percent_leased"] ? tempResults[i]["percent_leased"] : "",
					electric_price_sf:
						parseFloat(tempResults[i]["electric_price_sf"]) > 0.0
							? tempResults[i]["electric_price_sf"]
							: "",
					gross_price_sf:
						parseFloat(tempResults[i]["gross_price_sf"]) > 0.0 ? tempResults[i]["gross_price_sf"] : "",

					leasing_company: tempResults[i]["leasing_company"] ? tempResults[i]["leasing_company"] : "",
					status:
						tempResults[i]["status"] === "Existing" ||
						tempResults[i]["status"] === "Under Construction" ||
						tempResults[i]["status"] === "Proposed"
							? tempResults[i]["status"]
							: "",
					is_current: tempResults[i]["is_current"] === true ? true : false,
				});
				tempLocation.push(apiResponse);
			} else {
				const firstAvailableAddress =
					apiData?.batchItems[i]?.response?.results?.[0]?.address?.freeformAddress ||
					"No matching address found";

				const failedMatchInfo = {
					"Original Address": `${tempData[i + 1][0]}, ${tempData[i + 1][1]}, ${tempData[i + 1][2]}, ${
						tempData[i + 1][3]
					}`,
					"Mapped Address": firstAvailableAddress,
					"Condition 1 (City + State + ZIP)": false,
					"Condition 2 (Street + ZIP)": false,
				};
				console.table(failedMatchInfo);

				error.push(`${i + 1} - ${propertyAddress}, ${city}, ${state}, ${zip} not available`);
			}
		}
	}
	if (error?.length > 1) {
		setCsvError(error);
	}
	if (tempLocation?.length > 0) {
		setLocation({
			...location,
			proposedLocations: tempLocation,
		});
		setProposedLocationIndex(tempDescription?.length);
		setLocationDescription(tempDescription);
	}
	setCSVProcessingLoading(false);
};

export const handleToggleCheckboxSocCode = (code, selectedSocCode, setSelectedSocCode) => {
	const exists = selectedSocCode.find((item) => item.id === code.id);

	if (exists) {
		setSelectedSocCode(selectedSocCode.filter((item) => item.id !== code.id));
	} else {
		setSelectedSocCode([...selectedSocCode, code]);
	}
};

export const logFormData = (formData) => {
	console.log("Form Data Contents:");
	for (let [key, value] of formData.entries()) {
		if (value instanceof File) {
			console.log(`${key}: File - ${value.name}`);
		} else if (typeof value === "object") {
			console.log(`${key}: ${JSON.stringify(value)}`);
		} else {
			console.log(`${key}: ${value}`);
		}
	}
};

export const getSocCodeList = async (setSocCodeList) => {
	const res = await getSOCCode();
	if (res?.code === 200) {
		setSocCodeList(res.data);
	}
};

export const createDashBoard = async (
	dashboardTitle,
	location,
	useOnetSearch,
	currentLocationDescription,
	csvFile,
	customersFile,
	locationPDFFilesNames,
	locationDescription,
	selectedSocCode,
	setLoading,
	setTrigger,
	setStep,
	setOpenBiModal,
	currentDashboardData,
	officeLocationType,
	dispatch,
) => {
	setLoading(true);
	setTrigger(false);
	const bodyFormData = new FormData();
	const currentDashboardId = currentDashboardData?.id ?? null;

	currentDashboardId && bodyFormData.append("currentDashboardId", currentDashboardId);
	bodyFormData.append(
		"dashboard_title",
		dashboardTitle !== "" ? dashboardTitle : location?.currentLocation?.address?.freeformAddress,
	);
	bodyFormData.append("is_industrial_location_type", officeLocationType ? true : false);
	bodyFormData.append("use_search", useOnetSearch ? true : false);
	bodyFormData.append("created_from", currentDashboardData?._id ? "score_insights" : "");
	bodyFormData.append("created_from_id", currentDashboardData?._id ?? "none");

	if (location?.currentLocation?.address?.freeformAddress) {
		bodyFormData.append(
			"current_location",
			location?.currentLocation?.address?.freeformAddress ? location.currentLocation.address.freeformAddress : "",
		);
		bodyFormData.append("latitude", location.currentLocation.position.lat || "");
		bodyFormData.append("longitude", location.currentLocation.position.lon || "");
		bodyFormData.append("opex_value_current", currentLocationDescription.opex_value_current || "");
		bodyFormData.append("is_current_current", currentLocationDescription.is_current || false);
		bodyFormData.append(
			"status_current",
			currentLocationDescription ? currentLocationDescription.status || "" : "",
		);
		bodyFormData.append(
			"leasing_company_current",
			currentLocationDescription ? currentLocationDescription.leasing_company || "" : "",
		);
	}

	if (csvFile) {
		bodyFormData.append("employee_csv_path", csvFile);
	}
	if (customersFile) {
		bodyFormData.append("customer_csv_path", customersFile);
	}

	if (locationPDFFilesNames) {
		locationPDFFilesNames.forEach((locationData) => {
			if (locationData.files && locationData.files.length > 0) {
				const pdfsForLocation = locationData.files.map((file) => ({
					name: file.name || file,
					url: file.url || file,
				}));
				bodyFormData.append(`pdfs[${locationData.location_index}]`, JSON.stringify(pdfsForLocation));
			}
		});
	}

	location.proposedLocations.forEach((item, index) => {
		if (item?.address || (item?.position?.lat && item?.position?.lon)) {
			const locationName = item?.lat_lng_name || item?.address?.freeformAddress || "";
			bodyFormData.append("proposed_location[]", locationName);

			const zipCode = item.address?.postalCode ? item.address.postalCode.split(",")[0].trim() : "";

			bodyFormData.append("zip_code[]", zipCode);
			bodyFormData.append("proposed_location_lat[]", item.position?.lat || "");
			bodyFormData.append("proposed_location_long[]", item.position?.lon || "");

			bodyFormData.append(
				"opex_value[]",
				locationDescription[index] ? locationDescription[index].opex_value || "" : "",
			);
			bodyFormData.append(
				"year_built[]",
				locationDescription[index] ? locationDescription[index].year_built || "" : "",
			);
			bodyFormData.append(
				"building_sf[]",
				locationDescription[index] ? locationDescription[index].building_sf || "" : "",
			);
			bodyFormData.append(
				"parking_ratio[]",
				locationDescription[index] ? locationDescription[index].parking_ratio || "" : "",
			);
			bodyFormData.append(
				"available_sf[]",
				locationDescription[index] ? locationDescription[index].available_sf || "" : "",
			);
			bodyFormData.append(
				"office_sf[]",
				locationDescription[index] ? locationDescription[index].office_sf || "" : "",
			);
			bodyFormData.append(
				"clear_height[]",
				locationDescription[index] ? locationDescription[index].clear_height || "" : "",
			);
			bodyFormData.append(
				"car_trailer_parking[]",
				locationDescription[index] ? locationDescription[index].car_trailer_parking || "" : "",
			);
			bodyFormData.append(
				"docs_grade_level_doors[]",
				locationDescription[index] ? locationDescription[index].docs_grade_level_doors || "" : "",
			);
			bodyFormData.append(
				"location_suite[]",
				locationDescription[index] ? locationDescription[index].location_suite || "" : "",
			);
			bodyFormData.append(
				"percent_leased[]",
				locationDescription[index] ? locationDescription[index].percent_leased || "" : "",
			);
			bodyFormData.append(
				"base_rent[]",
				locationDescription[index] ? locationDescription[index].base_rent || "" : "",
			);
			bodyFormData.append(
				"electric_price_sf[]",
				locationDescription[index] ? locationDescription[index].electric_price_sf || "" : "",
			);
			bodyFormData.append(
				"gross_price_sf[]",
				locationDescription[index] ? locationDescription[index].gross_price_sf || "" : "",
			);
			bodyFormData.append(
				"is_current[]",
				locationDescription[index] ? locationDescription[index].is_current || false : false,
			);
			bodyFormData.append("status[]", locationDescription[index] ? locationDescription[index].status || "" : "");
			bodyFormData.append(
				"leasing_company[]",
				locationDescription[index] ? locationDescription[index].leasing_company || "" : "",
			);
			bodyFormData.append("notes[]", locationDescription[index] ? locationDescription[index].notes || "" : "");

			bodyFormData.append("order[]", index);
		}
	});

	selectedSocCode.forEach((item) => {
		const socCodeData = useOnetSearch ? JSON.stringify(item) : item.id;
		bodyFormData.append("soc_code[]", socCodeData || "");
	});
	// logFormData(bodyFormData);

	if (currentDashboardId && bodyFormData.get("created_from") !== "score_insights") {
		const res = await editDashboardDetails(bodyFormData);
		if (res?.result === "ok") {
			setStep(1);
		}
	} else {
		const res = await createDashboard(bodyFormData);
		if (res?.code === 200) {
			dispatch(
				ToastShow({
					message: "Successfully created Business Insights dashboard",
					type: "success",
				}),
			);
			setOpenBiModal(false);
			setStep(1);
		}
	}
};

export const validateStep = (validationError, setValidationError, step, dashboardTitle, location, selectedSocCode) => {
	let isValid = true;
	const newValidationError = { ...validationError };

	if (step === 1) {
		if (!dashboardTitle.trim()) {
			newValidationError.title.error = true;
			isValid = false;
		} else {
			newValidationError.title.error = false;
		}

		const hasValidLocation =
			location.proposedLocations.length > 0 &&
			location.proposedLocations.some((loc) => {
				const hasValidAddress =
					loc && loc.address && loc.address.freeformAddress && loc.address.freeformAddress.trim() !== "";
				const hasValidCoordinates =
					loc &&
					loc.position &&
					loc.position.lat !== null &&
					loc.position.lat !== undefined &&
					loc.position.lon !== null &&
					loc.position.lon !== undefined;

				// Location is valid if it has either a valid address with coordinates OR just valid coordinates
				return (hasValidAddress && hasValidCoordinates) || hasValidCoordinates;
			});

		if (!hasValidLocation) {
			newValidationError.location.error = true;
			isValid = false;
		} else {
			newValidationError.location.error = false;
		}
	}

	if (step === 4) {
		if (selectedSocCode.length === 0) {
			newValidationError.socCode.error = true;
			isValid = false;
		} else {
			newValidationError.socCode.error = false;
		}
	}

	setValidationError(newValidationError);
	return isValid;
};

export const checkUrlsForMaliciousContent = async (urls) => {
	let threatUrls = [];
	urls.map((url) => {
		threatUrls.push({ url: url });
	});

	var myHeaders = new Headers();
	myHeaders.append("Content-Type", "application/json");

	var raw = JSON.stringify({
		"client": {
			"clientId": "bluechip-insights",
			"clientVersion": "1.5.2",
		},
		"threatInfo": {
			"threatTypes": ["MALWARE", "SOCIAL_ENGINEERING", "POTENTIALLY_HARMFUL_APPLICATION", "UNWANTED_SOFTWARE"],
			"platformTypes": ["ANY_PLATFORM"],
			"threatEntryTypes": ["URL"],
			"threatEntries": threatUrls,
		},
	});

	var requestOptions = {
		method: "POST",
		headers: myHeaders,
		body: raw,
		redirect: "follow",
	};

	await fetch(
		"https://safebrowsing.googleapis.com/v4/threatMatches:find?key=" +
			import.meta.env.VITE_REACT_GOOGLE_SAFE_BROWSING_API_KEY,
		requestOptions,
	)
		.then((response) => response.text())
		.then((result) => {
			// If the result is not empty, then we have a malicious URL.
			if (!isEmptyObject(JSON.parse(result))) {
				// We need to show a warning to the user.
				setErrorLocationValidation((prevArray) => [
					...prevArray,
					{ type: "currentLocation", message: "* Please remove the malicious URL from the notes" },
				]);
			}
		})
		.catch((error) => console.warn("error", error));
};

export const handleLocationPDFOnChangeFile = (
	locationFiles,
	setLocationPDFFilesErrorUpload,
	setLocationPDFFilesNames,
	uploadLocationPDFS,
	removeLocationPDFS,
	setLocationPDFFiles,
) => {
	if (locationFiles.type === "existing") {
		setLocationPDFFilesNames(locationFiles.pdfs);
		return;
	}
	if (locationFiles && locationFiles.files && locationFiles.files?.length > 0) {
		setLocationPDFFilesErrorUpload();
		uploadLocationPDFS(locationFiles.location_index, locationFiles.files);
	} else if (locationFiles.e && locationFiles.e.target && locationFiles.e.target.files) {
		setLocationPDFFilesErrorUpload();
		uploadLocationPDFS(locationFiles.location_index, locationFiles.e.target.files);
	} else if (locationFiles.e && locationFiles.e.name) {
		setLocationPDFFilesErrorUpload();
		uploadLocationPDFS(locationFiles.e);
	} else {
		removeLocationPDFS(locationPDFFiles);
		setLocationPDFFiles();
		setLocationPDFFilesNames();
	}
};

export const uploadLocationPDFS = async (
	location_index,
	files,
	setlocationPDFFilesUpload,
	setLocationPDFFilesErrorUpload,
	setLocationPDFFilesNames,
	setLocationPDFFiles,
	validatePDFFiles,
) => {
	if (!files) return;

	let uploadedFiles = [];
	let uploadProgress = [];

	for (const [index, file] of Array.from(files).entries()) {
		const formData = new FormData();
		formData.append(`files[${location_index}][${index}]`, file);

		const config = {
			onUploadProgress: (progressEvent) => {
				const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
				uploadProgress[index] = progress;
				setlocationPDFFilesUpload({ ...uploadProgress });
			},
		};

		const res = await validatePDFFiles(formData, config.onUploadProgress);

		if (res?.status === "success") {
			const uploadedFile = res?.data[0];
			uploadedFiles.push(uploadedFile);
			uploadProgress[index] = 100; // Set to 100% when upload is complete
			setlocationPDFFilesUpload({ ...uploadProgress });
		} else {
			setLocationPDFFilesErrorUpload(res.message);
			break;
		}
	}

	if (uploadedFiles.length > 0) {
		setLocationPDFFilesNames((prevFiles) => {
			const existingLocation = prevFiles.find((loc) => loc.location_index === location_index);

			if (existingLocation) {
				return prevFiles.map((loc) =>
					loc.location_index === location_index ? { ...loc, files: [...loc.files, ...uploadedFiles] } : loc,
				);
			} else {
				return [...prevFiles, { location_index, files: uploadedFiles }];
			}
		});

		setTimeout(() => {
			setLocationPDFFiles((prevFiles) => [...prevFiles, ...uploadedFiles]);
		}, 0);
	}
};

export const removeUploadedFile = (fileToRemove, locationPDFFilesNames, setLocationPDFFilesNames) => {
	const updatedArray = locationPDFFilesNames
		.map((locationFile) => {
			const updatedFiles = locationFile.files.filter((file) => file !== fileToRemove);
			return { ...locationFile, files: updatedFiles };
		})
		.filter((locationFile) => locationFile.files.length > 0);

	setLocationPDFFilesNames(updatedArray);
};

export const removeLocationPDFS = async (file, removeLocationPDFUploadedFile, dispatch, getDashboard, dashboardId) => {
	if (!file) return;

	const formData = new FormData();
	formData.append("pdf", JSON.stringify(file));

	const res = await removeLocationPDFUploadedFile(formData);
	if (res?.code === 200) {
		dispatch(getDashboard(dashboardId));
	}
};

export const getInitialLocationState = () => ({
	currentLocation: {},
	proposedLocations: [{}],
});

export const getInitialValidationErrorState = () => ({
	title: { error: null, message: "Please add a title to the dashboard" },
	location: { error: null, message: "At least one location is required" },
	socCode: { error: null, message: "Please select at least one SOC code" },
});

export const getInitialStepOneData = () => ({
	location: {
		currentLocation: {},
		proposedLocations: [{}],
	},
	locationDescription: [],
	proposedLocationIndex: 1,
});

const STEP_TITLES = {
	1: "Locations",
	2: "Employees",
	3: "Customers",
	4: "Labor Profiles (SOC)",
};

/**
 * Generates a formatted title string for a dashboard step.
 *
 * @param {number} step - The current step number
 * @param {string} customTitle - Prefix for the dashboard
 * @returns {string|undefined} A formatted string combining the custom title and step information
 */
export const getStepTitle = (step, customTitle) => {
	const baseTitle = customTitle;
	const stepTitle = STEP_TITLES[step];
	if (customTitle) {
		return `${baseTitle} ${stepTitle} (step ${step} of 4)`;
	}
};

export const getModalCustomButtons = ({
	step,
	csvApproval,
	approvingCsvUpload,
	proceedWithCsvUpload,
	setProceedWithCsvUpload,
	setApprovingCsvUpload,
	setCSVProcessingLoading,
	validationError,
	setStep,
	handleNextStep,
	currentDashboardData,
}) => {
	const hasValidationError = (step) => {
		switch (step) {
			case 1:
				return validationError.title.error || validationError.location.error;
			case 4:
				return validationError.socCode.error;
			default:
				return false;
		}
	};

	return (
		<div key='save-div' className='justify-end d-flex'>
			{csvApproval !== null && approvingCsvUpload === true && proceedWithCsvUpload === false ? (
				<>
					<button
						style={{ marginRight: "24px" }}
						className='btn primary'
						disabled={csvApproval.some(
							(row) => !row.approved || row.latitude == null || row.longitude == null,
						)}
						onClick={() => {
							setProceedWithCsvUpload(true);
							setApprovingCsvUpload(false);
						}}
					>
						Approve
					</button>
					<button
						className='btn secondary outline'
						onClick={() => {
							setApprovingCsvUpload(false);
							setProceedWithCsvUpload(false);
							setCSVProcessingLoading(false);
						}}
					>
						Cancel
					</button>
				</>
			) : (
				<>
					{step === 1 && (
						<span
							className='validation-errors d-flex align-items-center'
							style={{ gap: "15px", marginRight: "15px" }}
						>
							{validationError.title.error && (
								<span className='text-danger'>{validationError.title.message}</span>
							)}
							{validationError.location.error && (
								<span className='text-danger' style={{ color: "red" }}>
									{validationError.location.message}
								</span>
							)}
						</span>
					)}

					{step !== 1 && (
						<button
							onClick={() => {
								setStep((prevStep) => Math.max(1, prevStep - 1));
							}}
							style={{ marginRight: "24px" }}
							className='btn secondary outline'
						>
							Back
						</button>
					)}
					{step !== 4 && (
						<button
							onClick={handleNextStep}
							style={{ marginRight: "24px" }}
							className={`btn secondary outline ${hasValidationError(step) ? "error" : ""}`}
						>
							Next
						</button>
					)}
					{step === 4 && (
						<button
							onClick={handleNextStep}
							className={`min-width-fit-content btn secondary outline ${
								hasValidationError(step) ? "error" : ""
							}`}
						>
							{currentDashboardData?.id ? "Update" : "Create"} Business Insights Dashboard
						</button>
					)}
				</>
			)}
		</div>
	);
};

export const handleMonetaryChange = (value, index, handleOnDiscriptionChange, fieldName) => {
	if (value === "") {
		handleOnDiscriptionChange(fieldName, "", index);
		return;
	}

	const basicRegex = /^\d*\.?\d*$/;
	if (!basicRegex.test(value)) {
		return;
	}

	const parts = value.split(".");
	if (parts.length > 1 && parts[1].length > 2) {
		value = `${parts[0]}.${parts[1].substring(0, 2)}`;
	}

	handleOnDiscriptionChange(fieldName, value, index);
};

export const formatMonetaryValue = (value) => {
	if (!value && value !== 0) return "";

	const cleanValue = value.toString().replace(/[$,]/g, "");

	const numericValue = parseFloat(cleanValue);
	if (isNaN(numericValue)) return "";

	return numericValue.toFixed(2);
};

/**
 * Validates and formats latitude or longitude values
 * @param {string|number} value - The coordinate value to validate
 * @param {string} type - Either 'lat' for latitude or 'lon' for longitude
 * @returns {number|null} - The validated coordinate as a number, or null if invalid
 */
export const validateCoordinate = (value, type) => {
	if (value === "" || value === null || value === undefined) return null;

	let numericValue = value.toString().trim();

	if (!/^-?\d*\.?\d*$/.test(numericValue)) {
		numericValue = numericValue.replace(/[^\d.-]/g, "");
	}

	const coord = parseFloat(numericValue);

	if (isNaN(coord)) return null;

	if (type === "lat" && (coord < -90 || coord > 90)) {
		return null;
	}

	if (type === "lon" && (coord < -180 || coord > 180)) {
		return null;
	}

	return coord;
};

export const formatMonetaryDisplay = (value) => {
	if (!value && value !== 0) return "";

	const numericValue = parseFloat(value);
	if (isNaN(numericValue)) return "";

	return `$${addCommas(numericValue.toFixed(2))}`;
};

export const formatOnBlur = (value) => {
	if (!value && value !== 0) return "";

	const numericValue = parseFloat(value);
	if (isNaN(numericValue)) return "";

	return numericValue.toFixed(2);
};
